import type { ReactNode } from 'react';

import type { ShippingInfo } from '../api/client.ts';
import { isPossiblePhone, parseInternationalNumber, pastedPhonePatch } from '../lib/phone-input.ts';

import { CountryPicker } from './country-picker.tsx';

// Single source for the physical-card shipping form, shared by /new-card
// (collapsible section) and /ship (standalone screen). The two screens used
// to carry diverging copies: /ship capped city at 20 chars and names at 50
// while the backend DTO allows city 100 / names 20, so one path let users
// submit values the other (and the server) rejected. Field rules here match
// the bot's ShipCardDto limits exactly.

const DIAL_CODE_REGEX = /^\d{1,4}$/;
const COUNTRY_CODE_REGEX = /^[A-Za-z]{2}$/;
const SHIP_PHONE_REGEX = /^[\d\s-]+$/;

export interface ShippingFormState {
  firstName: string;
  lastName: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  country: string;
  recipientDialCode: string;
  recipientPhone: string;
  email: string;
  courierType?: string;
  courierName?: string;
}

export interface ShippingFieldErrors {
  firstName?: string;
  lastName?: string;
  line1?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  recipientDialCode?: string;
  recipientPhone?: string;
  email?: string;
  courierType?: string;
  courierName?: string;
}

export const blankShipping: ShippingFormState = {
  firstName: '',
  lastName: '',
  line1: '',
  line2: '',
  city: '',
  postalCode: '',
  country: '',
  recipientDialCode: '',
  recipientPhone: '',
  email: '',
  courierType: 'POSTAL',
  courierName: '',
};

// Every field a submit must mark as touched so collapsed errors surface.
export const allShippingFields: readonly (keyof ShippingFieldErrors)[] = [
  'firstName',
  'lastName',
  'line1',
  'city',
  'postalCode',
  'country',
  'recipientDialCode',
  'recipientPhone',
  'email',
  'courierType',
  'courierName',
];

export function validateShipping(s: ShippingFormState): ShippingFieldErrors {
  const errors: ShippingFieldErrors = {};
  if (s.firstName.trim() === '') errors.firstName = 'Required.';
  if (s.lastName.trim() === '') errors.lastName = 'Required.';
  if (s.line1.trim() === '') errors.line1 = 'Required.';
  if (s.city.trim() === '') errors.city = 'Required.';
  if (s.postalCode.trim() === '') errors.postalCode = 'Required.';
  if (!COUNTRY_CODE_REGEX.test(s.country)) errors.country = '2-letter ISO code (e.g. GB).';
  if (!DIAL_CODE_REGEX.test(s.recipientDialCode)) errors.recipientDialCode = '1 to 4 digits.';
  if (!SHIP_PHONE_REGEX.test(s.recipientPhone) || s.recipientPhone.trim() === '') {
    errors.recipientPhone = 'Digits, spaces or hyphens only.';
  } else if (
    DIAL_CODE_REGEX.test(s.recipientDialCode) &&
    !isPossiblePhone(s.recipientDialCode, s.recipientPhone.replace(/\D/g, ''))
  ) {
    // Catch wrong-length / merged-country-code numbers here so the provider
    // does not reject the shipment with an opaque "invalid phone number".
    errors.recipientPhone = 'Enter a valid phone number for this dial code.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email)) errors.email = 'Valid email required.';
  if (!s.courierType) errors.courierType = 'Select a delivery method.';
  if (s.courierType === 'COURIER' && !s.courierName) errors.courierName = 'Select a carrier.';
  return errors;
}

export const buildShippingPayload = (shipping: ShippingFormState): ShippingInfo => ({
  shippingAddress: {
    line1: shipping.line1.trim(),
    ...(shipping.line2.trim() !== '' && { line2: shipping.line2.trim() }),
    city: shipping.city.trim(),
    postalCode: shipping.postalCode.trim(),
    country: shipping.country.trim().toUpperCase(),
  },
  recipientFirstName: shipping.firstName.trim(),
  recipientLastName: shipping.lastName.trim(),
  recipientPhoneNumber: shipping.recipientPhone.trim(),
  recipientPhoneDialCode: Number(shipping.recipientDialCode),
  recipientEmail: shipping.email.trim(),
  courier: {
    type: shipping.courierType ?? 'POSTAL',
    ...(shipping.courierType === 'COURIER' &&
      shipping.courierName !== '' && {
        name: shipping.courierName,
      }),
  },
});

interface ShippingFieldsProps {
  readonly shipping: ShippingFormState;
  /** Merge a partial update into the caller's form state. */
  readonly onPatch: (patch: Partial<ShippingFormState>) => void;
  /** Mark fields as touched in the caller's touched map. */
  readonly onTouch: (...fields: readonly (keyof ShippingFieldErrors)[]) => void;
  /** Error to display for a field, or undefined to stay quiet. */
  readonly showError: (field: keyof ShippingFieldErrors) => string | undefined;
}

const FieldHint = ({ id, error }: { readonly id: string; readonly error: string | undefined }): ReactNode =>
  error !== undefined ? (
    <span id={id} className="new-card-field-hint">
      {error}
    </span>
  ) : null;

export const ShippingFields = ({
  shipping,
  onPatch,
  onTouch,
  showError,
}: ShippingFieldsProps): ReactNode => (
  <>
    <div className="new-card-duo">
      <label className="new-card-field" htmlFor="ship-first-name">
        <span className="new-card-field-label">First name</span>
        <input
          id="ship-first-name"
          type="text"
          maxLength={20}
          value={shipping.firstName}
          onChange={(e): void => {
            onPatch({ firstName: e.target.value });
          }}
          onBlur={(): void => {
            onTouch('firstName');
          }}
          aria-invalid={showError('firstName') !== undefined}
          aria-describedby={showError('firstName') !== undefined ? 'ship-firstName-hint' : undefined}
          aria-required
        />
        <FieldHint id="ship-firstName-hint" error={showError('firstName')} />
      </label>
      <label className="new-card-field" htmlFor="ship-last-name">
        <span className="new-card-field-label">Last name</span>
        <input
          id="ship-last-name"
          type="text"
          maxLength={20}
          value={shipping.lastName}
          onChange={(e): void => {
            onPatch({ lastName: e.target.value });
          }}
          onBlur={(): void => {
            onTouch('lastName');
          }}
          aria-invalid={showError('lastName') !== undefined}
          aria-describedby={showError('lastName') !== undefined ? 'ship-lastName-hint' : undefined}
          aria-required
        />
        <FieldHint id="ship-lastName-hint" error={showError('lastName')} />
      </label>
    </div>

    <label className="new-card-field" htmlFor="ship-line1">
      <span className="new-card-field-label">Address line 1</span>
      <input
        id="ship-line1"
        type="text"
        maxLength={100}
        value={shipping.line1}
        onChange={(e): void => {
          onPatch({ line1: e.target.value });
        }}
        onBlur={(): void => {
          onTouch('line1');
        }}
        aria-invalid={showError('line1') !== undefined}
        aria-describedby={showError('line1') !== undefined ? 'ship-line1-hint' : undefined}
        aria-required
      />
      <FieldHint id="ship-line1-hint" error={showError('line1')} />
    </label>

    <label className="new-card-field" htmlFor="ship-line2">
      <span className="new-card-field-label">Address line 2 (optional)</span>
      <input
        id="ship-line2"
        type="text"
        maxLength={100}
        value={shipping.line2}
        onChange={(e): void => {
          onPatch({ line2: e.target.value });
        }}
      />
    </label>

    <div className="new-card-duo">
      <label className="new-card-field" htmlFor="ship-city">
        <span className="new-card-field-label">City</span>
        <input
          id="ship-city"
          type="text"
          maxLength={100}
          value={shipping.city}
          onChange={(e): void => {
            onPatch({ city: e.target.value });
          }}
          onBlur={(): void => {
            onTouch('city');
          }}
          aria-invalid={showError('city') !== undefined}
          aria-describedby={showError('city') !== undefined ? 'ship-city-hint' : undefined}
          aria-required
        />
        <FieldHint id="ship-city-hint" error={showError('city')} />
      </label>
      <label className="new-card-field" htmlFor="ship-postal">
        <span className="new-card-field-label">Postal code</span>
        <input
          id="ship-postal"
          type="text"
          maxLength={20}
          value={shipping.postalCode}
          onChange={(e): void => {
            onPatch({ postalCode: e.target.value });
          }}
          onBlur={(): void => {
            onTouch('postalCode');
          }}
          aria-invalid={showError('postalCode') !== undefined}
          aria-describedby={showError('postalCode') !== undefined ? 'ship-postalCode-hint' : undefined}
          aria-required
        />
        <FieldHint id="ship-postalCode-hint" error={showError('postalCode')} />
      </label>
    </div>

    <label className="new-card-field" htmlFor="ship-country">
      <span className="new-card-field-label">Country</span>
      <CountryPicker
        id="ship-country"
        value={shipping.country}
        onChange={(country): void => {
          onPatch({ country });
        }}
        onBlur={(): void => {
          onTouch('country');
        }}
        aria-invalid={showError('country') !== undefined}
        aria-describedby={showError('country') !== undefined ? 'ship-country-hint' : undefined}
      />
      <FieldHint id="ship-country-hint" error={showError('country')} />
    </label>

    <div className="new-card-phone">
      <label className="new-card-field new-card-field-dial" htmlFor="ship-rec-dial">
        <span className="new-card-field-label">Dial code</span>
        <input
          id="ship-rec-dial"
          type="tel"
          /* inputMode "tel" (not "numeric"): the numeric keypad on some
             Android keyboards swallows a clipboard paste, so the field
             never receives the pasted number. The tel keypad accepts it. */
          inputMode="tel"
          /* Roomy enough that a pasted full international number reaches
             onChange intact instead of being clipped to 4 chars before
             the split runs; the value is re-capped to 4 digits below. */
          maxLength={18}
          placeholder="44"
          autoComplete="tel-country-code"
          value={shipping.recipientDialCode}
          onPaste={(e): void => {
            e.preventDefault();
            const patch = pastedPhonePatch(e.clipboardData.getData('text'), 'dialCode');
            onPatch({
              ...(patch.dialCode !== undefined && { recipientDialCode: patch.dialCode }),
              ...(patch.phoneNumber !== undefined && { recipientPhone: patch.phoneNumber }),
            });
            const touched: (keyof ShippingFieldErrors)[] = [];
            if (patch.dialCode !== undefined) touched.push('recipientDialCode');
            if (patch.phoneNumber !== undefined) touched.push('recipientPhone');
            onTouch(...touched);
          }}
          onChange={(e): void => {
            const split = parseInternationalNumber(e.target.value);
            if (split !== null) {
              onPatch({ recipientDialCode: split.dialCode, recipientPhone: split.phoneNumber });
              onTouch('recipientDialCode', 'recipientPhone');
              return;
            }
            onPatch({ recipientDialCode: e.target.value.replace(/\D/g, '').slice(0, 4) });
          }}
          onBlur={(): void => {
            onTouch('recipientDialCode');
          }}
          aria-invalid={showError('recipientDialCode') !== undefined}
          aria-describedby={
            showError('recipientDialCode') !== undefined ? 'ship-recipientDialCode-hint' : undefined
          }
          aria-required
        />
        <FieldHint id="ship-recipientDialCode-hint" error={showError('recipientDialCode')} />
      </label>
      <label className="new-card-field new-card-field-phone" htmlFor="ship-rec-phone">
        <span className="new-card-field-label">Recipient phone</span>
        <input
          id="ship-rec-phone"
          type="tel"
          inputMode="tel"
          maxLength={20}
          autoComplete="tel-national"
          value={shipping.recipientPhone}
          onPaste={(e): void => {
            e.preventDefault();
            const patch = pastedPhonePatch(e.clipboardData.getData('text'), 'phoneNumber');
            onPatch({
              ...(patch.dialCode !== undefined && { recipientDialCode: patch.dialCode }),
              ...(patch.phoneNumber !== undefined && { recipientPhone: patch.phoneNumber }),
            });
            const touched: (keyof ShippingFieldErrors)[] = [];
            if (patch.dialCode !== undefined) touched.push('recipientDialCode');
            if (patch.phoneNumber !== undefined) touched.push('recipientPhone');
            onTouch(...touched);
          }}
          onChange={(e): void => {
            const split = parseInternationalNumber(e.target.value);
            if (split !== null) {
              onPatch({ recipientDialCode: split.dialCode, recipientPhone: split.phoneNumber });
              onTouch('recipientDialCode', 'recipientPhone');
              return;
            }
            onPatch({ recipientPhone: e.target.value });
          }}
          onBlur={(): void => {
            onTouch('recipientPhone');
          }}
          aria-invalid={showError('recipientPhone') !== undefined}
          aria-describedby={
            showError('recipientPhone') !== undefined ? 'ship-recipientPhone-hint' : undefined
          }
          aria-required
        />
        <FieldHint id="ship-recipientPhone-hint" error={showError('recipientPhone')} />
      </label>
    </div>

    <label className="new-card-field" htmlFor="ship-email">
      <span className="new-card-field-label">Recipient email</span>
      <input
        id="ship-email"
        type="email"
        maxLength={100}
        value={shipping.email}
        onChange={(e): void => {
          onPatch({ email: e.target.value });
        }}
        onBlur={(): void => {
          onTouch('email');
        }}
        aria-invalid={showError('email') !== undefined}
        aria-describedby={showError('email') !== undefined ? 'ship-email-hint' : undefined}
        aria-required
      />
      <FieldHint id="ship-email-hint" error={showError('email')} />
    </label>

    <fieldset className="new-card-type" aria-label="Delivery method">
      <legend className="new-card-field-label">Delivery method</legend>
      <div className="segmented">
        <button
          type="button"
          className={`segmented-btn${shipping.courierType === 'POSTAL' ? ' active' : ''}`}
          onClick={(): void => {
            onPatch({ courierType: 'POSTAL', courierName: '' });
            onTouch('courierType');
          }}
          aria-pressed={shipping.courierType === 'POSTAL'}
        >
          Postal
        </button>
        <button
          type="button"
          className={`segmented-btn${shipping.courierType === 'COURIER' ? ' active' : ''}`}
          onClick={(): void => {
            onPatch({ courierType: 'COURIER' });
            onTouch('courierType');
          }}
          aria-pressed={shipping.courierType === 'COURIER'}
        >
          Courier
        </button>
      </div>
      <FieldHint id="ship-courierType-hint" error={showError('courierType')} />
    </fieldset>

    {shipping.courierType === 'COURIER' ? (
      <fieldset className="new-card-type" aria-label="Carrier">
        <legend className="new-card-field-label">Carrier</legend>
        <div className="segmented">
          <button
            type="button"
            className={`segmented-btn${shipping.courierName === 'DHL' ? ' active' : ''}`}
            onClick={(): void => {
              onPatch({ courierName: 'DHL' });
              onTouch('courierName');
            }}
            aria-pressed={shipping.courierName === 'DHL'}
          >
            DHL
          </button>
          <button
            type="button"
            className={`segmented-btn${shipping.courierName === 'FEDEX' ? ' active' : ''}`}
            onClick={(): void => {
              onPatch({ courierName: 'FEDEX' });
              onTouch('courierName');
            }}
            aria-pressed={shipping.courierName === 'FEDEX'}
          >
            FedEx
          </button>
        </div>
        <FieldHint id="ship-courierName-hint" error={showError('courierName')} />
      </fieldset>
    ) : null}
  </>
);
